import { Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Helpers } from '@core/common-legacy';
import { HttpApiService } from '@core/http-legacy';
import { HttpRequestData } from '@core/http-types';
import { createEffectFactory } from '@core/ngrx-legacy';

import { EEOHttpService } from './eeo-http.service';
import { EEOActions, EEOCategoriesActions } from './eeo.store';

@Injectable({
  providedIn: 'root',
})
export class EEOEffects {
  public effects = createEffectFactory(EEOActions, this.actions$);

  public loadEEOConfiguration$ = this.effects(EEOActions.fetch, () =>
    this.eeoHttpService.loadEEOConfiguration(),
  );

  public updateEEOConfiguration$ = this.effects(EEOActions.update, ({ payload }) =>
    this.eeoHttpService
      .updateEEOConfiguration(payload.data)
      .pipe(map(data => ({ ...data, updated: payload.updated }))),
  );

  public sendEmailForSingleCandidate$ = this.effects(
    EEOActions.sendEmailForSingleCandidate,
    ({ payload }) =>
      this.eeoHttpService.sendEmailForSingleCandidate(payload).pipe(
        map(() =>
          EEOActions.sendEmailForSingleCandidateSuccess({
            payload: { candidateId: payload.candidateId },
          }),
        ),
        catchError(error => of(EEOActions.sendEmailForSingleCandidateFailed({ payload: error }))),
      ),
  );

  public loadPlacementsWithoutEEOData$ = this.effects(
    EEOActions.loadNoEEODataPlacements,
    ({ payload }) =>
      this.eeoHttpService.loadPlacementsWithoutEEOData(payload.candidateId).pipe(
        map(data => EEOActions.loadNoEEODataPlacementsSuccess({ payload: data })),
        catchError(error => of(EEOActions.loadNoEEODataPlacementsFailed({ payload: error }))),
      ),
  );

  public bulkSend$ = this.effects(EEOActions.bulkSend, ({ payload }) =>
    this.eeoHttpService
      .bulkSend({
        candidateIds: payload.candidateIds,
        emailSubject: payload.emailSubject,
        emailBodyHtml: payload.emailBodyHtml,
      })
      .pipe(
        map(() =>
          EEOActions.bulkSendSuccess({
            payload: { candidatesCount: payload.candidateIds.length },
          }),
        ),
        catchError(error => of(EEOActions.bulkSendFailed({ payload: error }))),
      ),
  );

  public bulkResend$ = this.effects(EEOActions.bulkResend, ({ payload }) =>
    this.eeoHttpService
      .bulkResend({
        candidateIds: payload.candidateIds,
        emailSubject: payload.emailSubject,
        emailBodyHtml: payload.emailBodyHtml,
      })
      .pipe(
        map(() =>
          EEOActions.bulkResendSuccess({
            payload: { candidatesCount: payload.candidateIds.length },
          }),
        ),
        catchError(error => of(EEOActions.bulkResendFailed({ payload: error }))),
      ),
  );

  public exportEEOReport$ = this.effects(EEOActions.exportEEOReport, ({ payload }) => {
    const url = this.httpRequestData.buildSignedUrl(
      '/eeo/data/export',
      this.httpApi.createSearchParams(Helpers.underscoreKeys(payload)),
    )!;
    window.open(url, '_blank');
    return of();
  });

  public bulkEnable$ = this.effects(EEOActions.bulkEnable, ({ payload }) =>
    this.eeoHttpService
      .bulkManageEeo({
        enabled: true,
        jobCategory: payload.jobCategory,
        offerIds: payload.offerIds,
      })
      .pipe(
        map(response => EEOActions.bulkEnableSuccess({ payload: { uuid: response.uuid } })),
        catchError(error => of(EEOActions.bulkEnableFailed({ payload: error }))),
      ),
  );

  public bulkDisable$ = this.effects(EEOActions.bulkDisable, ({ payload }) =>
    this.eeoHttpService
      .bulkManageEeo({
        enabled: false,
        offerIds: payload.offerIds,
      })
      .pipe(
        map(response => EEOActions.bulkDisableSuccess({ payload: { uuid: response.uuid } })),
        catchError(error => of(EEOActions.bulkDisableFailed({ payload: error }))),
      ),
  );

  constructor(
    private eeoHttpService: EEOHttpService,
    private actions$: Actions,
    private httpRequestData: HttpRequestData,
    protected httpApi: HttpApiService,
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class EEOCategoriesEffects {
  public effects = createEffectFactory(EEOCategoriesActions, this.actions$);

  public loadEEOCategories$ = this.effects(EEOCategoriesActions.fetch, () =>
    this.eeoHttpService.loadEEOCategories(),
  );

  constructor(
    private eeoHttpService: EEOHttpService,
    private actions$: Actions,
  ) {}
}
